import { supabase } from '../lib/supabase';
import { RoteiroMotorista, RoteiroItem } from '../types/roteiro';
import { OrdemDeServico } from '../types/ordemServico';
import { sanitizeDeep } from '../utils/textSanitizer';

export type StatusRoteiro = RoteiroMotorista['status'];

const STORAGE_KEY = 'voope_roteiros_locais';

function getLocalRoteiros(): RoteiroMotorista[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? sanitizeDeep(JSON.parse(saved)) : [];
  } catch (e) {
    return [];
  }
}

function saveLocalRoteiros(roteiros: RoteiroMotorista[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roteiros));
  } catch (e) {
    console.error('Erro ao salvar roteiro local:', e);
  }
}

export const roteiroService = {
  /**
   * Busca roteiros de um motorista para uma data ou todos
   */
  async getRoteirosMotorista(motoristaId?: number, data?: string): Promise<RoteiroMotorista[]> {
    try {
      let query = supabase
        .from('roteiro_motorista')
        .select(`
          *,
          roteiro_item (*)
        `)
        .order('data_roteiro', { ascending: false });

      if (motoristaId) {
        query = query.eq('motorista_id', motoristaId);
      }
      if (data) {
        query = query.eq('data_roteiro', data);
      }

      const { data: dbData, error } = await query;

      if (!error && dbData && dbData.length > 0) {
        const list = dbData.map((r: any) => ({
          ...r,
          itens: (r.roteiro_item || []).sort((a: any, b: any) => a.ordem_sequencia - b.ordem_sequencia)
        }));
        return sanitizeDeep(list);
      }
    } catch (e) {
      console.warn('Banco de dados ainda sem a tabela roteiro_motorista. Usando sincronização local.');
    }

    // Fallback LocalStorage sincronizado com os dados
    let roteiros = getLocalRoteiros();
    if (motoristaId) {
      roteiros = roteiros.filter(r => r.motorista_id === motoristaId);
    }
    if (data) {
      roteiros = roteiros.filter(r => r.data_roteiro === data);
    }
    return sanitizeDeep(roteiros);
  },

  /**
   * Salva ou atualiza um roteiro com sua lista ordenada de paradas
   */
  async salvarRoteiro(roteiro: Partial<RoteiroMotorista> & { itens: RoteiroItem[] }): Promise<RoteiroMotorista> {
    try {
      // Tenta gravar no Supabase se a tabela existir
      const { data: savedRoteiro, error: errRoteiro } = await supabase
        .from('roteiro_motorista')
        .upsert({
          id: roteiro.id || undefined,
          motorista_id: roteiro.motorista_id,
          veiculo_id: roteiro.veiculo_id,
          data_roteiro: roteiro.data_roteiro || new Date().toISOString().split('T')[0],
          nome_roteiro: roteiro.nome_roteiro || 'Roteiro Programado',
          status: roteiro.status || 'planejado',
          distancia_estimada_km: roteiro.distancia_estimada_km || 0,
          tempo_estimado_minutos: roteiro.tempo_estimado_minutos || 0,
          observacoes: roteiro.observacoes || '',
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (!errRoteiro && savedRoteiro) {
        // Remove itens antigos e insere os novos em ordem
        await supabase.from('roteiro_item').delete().eq('roteiro_id', savedRoteiro.id);

        const itensFormatados = roteiro.itens.map((item, idx) => ({
          roteiro_id: savedRoteiro.id,
          ordem_de_servico_id: item.ordem_de_servico_id,
          tipo_parada: item.tipo_parada,
          ordem_sequencia: idx + 1,
          prioridade: item.prioridade || 'normal',
          endereco: item.endereco,
          complemento: item.complemento || '',
          contato_local: item.contato_local || '',
          telefone_local: item.telefone_local || '',
          horario_janela_inicio: item.horario_janela_inicio || null,
          horario_janela_fim: item.horario_janela_fim || null,
          status_parada: item.status_parada || 'pendente',
          observacoes: item.observacoes || ''
        }));

        await supabase.from('roteiro_item').insert(itensFormatados);

        return {
          ...savedRoteiro,
          itens: roteiro.itens
        };
      }
    } catch (e) {
      console.warn('Salvando roteiro em armazenamento seguro...');
    }

    // Gravação segura no LocalStorage
    const locais = getLocalRoteiros();
    const id = roteiro.id || Date.now();
    const novoRoteiro: RoteiroMotorista = {
      id,
      motorista_id: roteiro.motorista_id || 1,
      motorista_nome: roteiro.motorista_nome || 'Motorista',
      veiculo_id: roteiro.veiculo_id,
      veiculo_placa: roteiro.veiculo_placa,
      data_roteiro: roteiro.data_roteiro || new Date().toISOString().split('T')[0],
      nome_roteiro: roteiro.nome_roteiro || `Roteiro #${id.toString().slice(-4)}`,
      status: roteiro.status || 'planejado',
      distancia_estimada_km: roteiro.distancia_estimada_km || 0,
      tempo_estimado_minutos: roteiro.tempo_estimado_minutos || 0,
      observacoes: roteiro.observacoes || '',
      itens: roteiro.itens.map((it, idx) => ({
        ...it,
        ordem_sequencia: idx + 1,
        roteiro_id: id
      }))
    };

    const idx = locais.findIndex(r => r.id === id);
    if (idx >= 0) {
      locais[idx] = novoRoteiro;
    } else {
      locais.unshift(novoRoteiro);
    }

    saveLocalRoteiros(locais);
    return novoRoteiro;
  },

  /**
   * Atualiza o status geral do roteiro (ex: planejado -> em_execucao -> concluido)
   */
  async atualizarStatusRoteiro(roteiroId: number, novoStatus: StatusRoteiro) {
    try {
      await supabase
        .from('roteiro_motorista')
        .update({
          status: novoStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', roteiroId);
    } catch (e) {
      // Fallback
    }

    const locais = getLocalRoteiros();
    const roteiro = locais.find(r => r.id === roteiroId);
    if (roteiro) {
      roteiro.status = novoStatus;
      saveLocalRoteiros(locais);
    }
  },

  /**
   * Atualiza o status de uma etapa / parada específica (pelo motorista)
   */
  async atualizarStatusParada(roteiroId: number, paradaIdOuSeq: number, novoStatus: 'pendente' | 'em_deslocamento' | 'chegou' | 'concluida') {
    try {
      await supabase
        .from('roteiro_item')
        .update({
          status_parada: novoStatus,
          hora_conclusao: novoStatus === 'concluida' ? new Date().toISOString() : null,
          hora_chegada: novoStatus === 'chegou' ? new Date().toISOString() : null,
        })
        .eq('roteiro_id', roteiroId)
        .eq('ordem_sequencia', paradaIdOuSeq);
    } catch (e) {
      // Ignora erro
    }

    const locais = getLocalRoteiros();
    const roteiro = locais.find(r => r.id === roteiroId);
    if (roteiro) {
      const item = roteiro.itens.find(it => it.id === paradaIdOuSeq || it.ordem_sequencia === paradaIdOuSeq);
      if (item) {
        item.status_parada = novoStatus;
        if (novoStatus === 'concluida') item.hora_conclusao = new Date().toLocaleTimeString('pt-BR');
        if (novoStatus === 'chegou') item.hora_chegada = new Date().toLocaleTimeString('pt-BR');
        saveLocalRoteiros(locais);
      }
    }
  },

  /**
   * Gera itens de paradas (Coleta + Entrega) a partir de uma lista de Ordens de Serviço
   */
  converterOSToParadas(osList: OrdemDeServico[]): RoteiroItem[] {
    const itens: RoteiroItem[] = [];

    osList.forEach((os, idx) => {
      // 1. Parada de Coleta
      if (os.retirada) {
        itens.push({
          ordem_de_servico_id: os.id,
          tipo_parada: 'coleta',
          ordem_sequencia: itens.length + 1,
          prioridade: 'normal',
          endereco: os.retirada,
          status_parada: 'pendente',
          descricao_carga: os.descricao || `OS #${os.id}`,
          valor_repasse: Number(os.repasse) || 0
        });
      }

      // 2. Parada de Entrega
      if (os.entrega) {
        itens.push({
          ordem_de_servico_id: os.id,
          tipo_parada: 'entrega',
          ordem_sequencia: itens.length + 1,
          prioridade: 'normal',
          endereco: os.entrega,
          status_parada: 'pendente',
          descricao_carga: os.descricao || `OS #${os.id}`,
          valor_repasse: Number(os.repasse) || 0
        });
      }
    });

    return itens;
  }
};
